import { CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { Box } from "@mui/system";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import dayjs, { Dayjs } from "dayjs";
import * as React from "react";
import { CameraItemDto } from "~/api/GeneratedApi";
import {
  useRecordedDates,
  useRecordedTimes,
} from "./hooks/useRecordedHlsStream";
import { VideoTimesList } from "./VideoTimesList";

type Props = { camera: CameraItemDto };
// const yearsAgo = new Date(
//   new Date().setFullYear(new Date().getFullYear() - 100),
// );
// const yearsAgo1 = new Date(
//   new Date().setFullYear(new Date().getFullYear() - 101),
// );
interface CustomPickerDayProps extends PickersDayProps<Dayjs> {
  hasContent: boolean;
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== "dayIsBetween" &&
    prop !== "isFirstDay" &&
    prop !== "isLastDay" &&
    prop !== "hasContent",
})<CustomPickerDayProps>(({ theme }) => ({
  "&.Mui-selected": {
    backgroundColor: `${theme.palette.info.light} !important`,
    border: "1px solid rgba(0, 0, 0, 0.6) !important",
  },
})) as React.ComponentType<CustomPickerDayProps>;

export const CameraRecord = ({ camera }: Props): JSX.Element => {
  const [dateValue, setDateValue] = React.useState<Dayjs | null>(null);
  const [dateSelected, setDateSelected] = React.useState<boolean>(false);
  const [month, setMonth] = React.useState<Dayjs>(dayjs());
  const { id: cameraId } = camera;
  const { availableDates, datesLoading } = useRecordedDates({
    cameraId,
    dateMax: month.endOf("month"),
    dateMin: month.startOf("month"),
  });

  const { availableTimes, availableTimesLoading } = useRecordedTimes({
    cameraId,
    date: dateValue,
  });

  const renderWeekPickerDay = React.useCallback(
    (
      date: Dayjs,
      _selectedDates: Array<Dayjs | null>,
      pickersDayProps: PickersDayProps<Dayjs>,
    ) => {
      const dayJsDates = availableDates?.map((d) => dayjs(d));

      const hasContent =
        dayJsDates?.some((d) => d.isSame(date, "day")) &&
        !pickersDayProps.outsideCurrentMonth;

      return (
        <CustomPickersDay
          {...pickersDayProps}
          disableMargin
          hasContent={!!hasContent}
          disabled={!hasContent}
        />
      );
    },
    [availableDates],
  );

  return (
    <Box>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <StaticDatePicker
          displayStaticWrapperAs="desktop"
          label="Week picker"
          value={dateValue}
          onChange={(newValue) => {
            setDateSelected(true);
            setDateValue(newValue);
          }}
          loading={datesLoading}
          renderLoading={() => <CircularProgress />}
          renderDay={renderWeekPickerDay}
          onMonthChange={(m) => setMonth(m)}
          renderInput={(params) => <TextField {...params} />}
          inputFormat="'Week of' MMM d"
        />
      </LocalizationProvider>
      {dateSelected && dateValue ? (
        <VideoTimesList
          key={dateValue.toString()}
          cameraId={cameraId}
          selectedDate={dateValue}
          availableTimes={availableTimes}
          availableTimesLoading={availableTimesLoading}
        />
      ) : null}
    </Box>
  );
};
